import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "@/components/Helmet";
import { useState, useEffect, useMemo } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Calendar, ArrowLeft, Linkedin, AlertTriangle, Shield, Link2, Facebook, Twitter, ArrowRight, FileWarning, Clock, List, Copy, Check, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getBlogPostBySlug, blogPosts } from "@/data/blogPosts";
import NewsletterSubscribe from "@/components/NewsletterSubscribe";

// Calculate reading time based on word count (~200 words per minute)
const calculateReadingTime = (content: string): number => {
  const wordCount = content.split(/\s+/).filter(word => word.length > 0).length;
  return Math.max(1, Math.ceil(wordCount / 200));
};

// Extract headings from content for table of contents
const extractHeadings = (content: string): { id: string; text: string; level: number }[] => {
  const lines = content.split('\n');
  const headings: { id: string; text: string; level: number }[] = [];
  
  lines.forEach(line => {
    if (line.startsWith('## ')) {
      const text = line.substring(3);
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      headings.push({ id, text, level: 2 });
    } else if (line.startsWith('### ')) {
      const text = line.substring(4);
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      headings.push({ id, text, level: 3 });
    }
  });
  
  return headings;
};

// CodeBlock component with copy functionality
const CodeBlock = ({ language, code }: { language: string; code: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Code copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-6 rounded-lg overflow-hidden border border-border/50">
      <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border/50">
        <span className="text-xs font-mono text-muted-foreground uppercase">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          title="Copy code"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-green-500" />
              <span className="text-green-500">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="p-4 bg-[hsl(210_15%_12%)] overflow-x-auto">
        <code className="text-sm font-mono text-[hsl(185_60%_70%)] leading-relaxed">
          {code}
        </code>
      </pre>
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();

  const slugRedirects: Record<string, string> = {
    "introducing-wordpress-security-services": "managed-wordpress-security-service",
    "security-beyond-virtual-patching": "security-beyond-automated-rules",
  };

  const slugValue = slug ?? "";
  const redirectedSlug = slugValue ? slugRedirects[slugValue] : undefined;

  const post = slugValue ? getBlogPostBySlug(slugValue) : null;

  const siteUrl = "https://shadowscan.com.au";
  const postUrl = `${siteUrl}/blog/${slugValue}`;
  const ogImage = `${siteUrl}/og-blog.png`;
  const metaDescription =
    post && post.excerpt.length > 157 ? `${post.excerpt.slice(0, 157)}…` : post?.excerpt ?? "";
  const pageTitle =
    post
      ? post.title.length > 40
        ? `${post.title.slice(0, 40)}… | ShadowScan Blog`
        : `${post.title} | ShadowScan Blog`
      : "ShadowScan Blog";

  // Determine if this is a threat/vulnerability post
  const titleLower = post?.title.toLowerCase() ?? "";
  const isThreatPost = titleLower.includes('vulnerability') || 
                       titleLower.includes('exploit') ||
                       titleLower.includes('attack');

  // Calculate reading time and extract headings
  const content = post?.content ?? "";
  const readingTime = useMemo(() => calculateReadingTime(content), [content]);
  const headings = useMemo(() => extractHeadings(content), [content]);
  const showToc = headings.length >= 3; // Show ToC for posts with 3+ headings
  
  // Track active heading for ToC highlighting
  const [activeHeading, setActiveHeading] = useState<string>('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  useEffect(() => {
    if (!showToc) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -80% 0px' }
    );
    
    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    
    return () => observer.disconnect();
  }, [headings, showToc]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!slugValue) {
    return <Navigate to="/blog" replace />;
  }

  if (redirectedSlug) {
    return <Navigate to={`/blog/${redirectedSlug}`} replace />;
  }

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        
        {/* Open Graph / LinkedIn */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={postUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="ShadowScan" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={postUrl} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={ogImage} />

        {/* BlogPosting structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "@id": postUrl,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": postUrl
            },
            headline: post.title,
            description: post.excerpt,
            datePublished: new Date(post.date).toISOString(),
            dateModified: new Date(post.date).toISOString(),
            image: ogImage,
            author: {
              "@type": "Organization",
              name: "ShadowScan SOC Team",
              url: "https://shadowscan.com.au/about"
            },
            publisher: {
              "@type": "Organization",
              "@id": "https://shadowscan.com.au/#organization",
              name: "ShadowScan",
              logo: {
                "@type": "ImageObject",
                url: "https://shadowscan.com.au/og-image.png",
                width: 1200,
                height: 630
              }
            },
            isPartOf: {
              "@type": "Blog",
              "@id": "https://shadowscan.com.au/blog/#blog",
              name: "ShadowScan Security Blog"
            },
            articleSection: isThreatPost ? "Threat Intelligence" : "Security Updates",
            keywords: "WordPress security, vulnerability, threat intelligence, malware response"
          })}
        </script>
      </Helmet>

      <Navigation />
      
      <main className="flex-grow pt-24">
        {/* Hero header */}
        <div className="relative py-12 overflow-hidden border-b border-border">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent -z-10" />
          
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              {/* Back to Blog */}
              <Button variant="ghost" className="mb-6 text-muted-foreground hover:text-foreground hover:bg-secondary" asChild>
                <Link to="/blog">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Blog
                </Link>
              </Button>

              {/* Article Header */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  {isThreatPost ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full">
                      <AlertTriangle className="h-4 w-4 text-amber-400" />
                      <span className="text-xs font-mono text-amber-400">THREAT_ALERT</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30 rounded-full">
                      <Shield className="h-4 w-4 text-primary" />
                      <span className="text-xs font-mono text-primary">SECURITY_UPDATE</span>
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                      <Calendar className="h-4 w-4" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                      <Clock className="h-4 w-4" />
                      <span>{readingTime} min read</span>
                    </div>
                  </div>
                </div>
                
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold leading-tight">{post.title}</h1>
                <p className="text-lg text-muted-foreground leading-relaxed">{post.excerpt}</p>
                
                {/* Share Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground mr-2">Share:</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 border-primary/30 hover:border-primary hover:bg-primary/10"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success("Link copied to clipboard");
                    }}
                    title="Copy link"
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 border-primary/30 hover:border-[#0077B5] hover:bg-[#0077B5]/10"
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      window.open(
                        `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
                        '_blank',
                        'width=600,height=600'
                      );
                    }}
                    title="Share on LinkedIn"
                  >
                    <Linkedin className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 border-primary/30 hover:border-foreground hover:bg-foreground/10"
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      const text = encodeURIComponent(post.title);
                      window.open(
                        `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
                        '_blank',
                        'width=600,height=400'
                      );
                    }}
                    title="Share on X"
                  >
                    <Twitter className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 border-primary/30 hover:border-[#1877F2] hover:bg-[#1877F2]/10"
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      window.open(
                        `https://www.facebook.com/sharer/sharer.php?u=${url}`,
                        '_blank',
                        'width=600,height=400'
                      );
                    }}
                    title="Share on Facebook"
                  >
                    <Facebook className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 border-primary/30 hover:border-[#FF4500] hover:bg-[#FF4500]/10"
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      const title = encodeURIComponent(post.title);
                      window.open(
                        `https://reddit.com/submit?url=${url}&title=${title}`,
                        '_blank',
                        'width=600,height=600'
                      );
                    }}
                    title="Share on Reddit"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <article className="py-12">
          <div className="container mx-auto px-4">
            <div className={`mx-auto ${showToc ? 'max-w-5xl flex gap-8' : 'max-w-3xl'}`}>
              {/* Table of Contents Sidebar */}
              {showToc && (
                <aside className="hidden lg:block w-64 flex-shrink-0">
                  <div className="sticky top-24">
                    <div className="p-4 rounded-lg border border-border bg-card/50">
                      <div className="flex items-center gap-2 mb-4 text-sm font-heading font-bold text-foreground">
                        <List className="h-4 w-4 text-primary" />
                        <span>Table of Contents</span>
                      </div>
                      <nav className="space-y-1">
                        {headings.map(({ id, text, level }) => (
                          <a
                            key={id}
                            href={`#${id}`}
                            className={`block text-sm py-1.5 px-2 rounded transition-colors ${
                              level === 3 ? 'ml-3' : ''
                            } ${
                              activeHeading === id
                                ? 'text-primary bg-primary/10 font-medium'
                                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                            }}
                          >
                            {text}
                          </a>
                        ))}
                      </nav>
                    </div>
                  </div>
                </aside>
              )}
              
              <div className={showToc ? 'flex-1 max-w-3xl' : ''}>
              {/* Article Content */}
              <div className="prose prose-invert prose-lg max-w-none prose-headings:font-heading prose-headings:font-bold prose-p:text-muted-foreground prose-strong:text-foreground prose-li:text-muted-foreground prose-a:text-primary hover:prose-a:text-primary/80">
                {(() => {
                  const lines = post.content.split('\n');
                  const nodes: React.ReactNode[] = [];

                  const renderInline = (text: string) => {
                    const parts: React.ReactNode[] = [];
                    // Added inline code support with backticks
                    const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|`[^`]+`)/g;
                    let lastIndex = 0;
                    let match: RegExpExecArray | null;
                    while ((match = regex.exec(text)) !== null) {
                      if (match.index > lastIndex) {
                        parts.push(text.slice(lastIndex, match.index));
                      }
                      const token = match[0];
                      if (token.startsWith('**')) {
                        parts.push(
                          <strong key={`${match.index}-bold`} className="text-foreground font-semibold">
                            {token.slice(2, -2)}
                          </strong>
                        );
                      } else if (token.startsWith('`') && token.endsWith('`')) {
                        // Inline code
                        parts.push(
                          <code key={`${match.index}-code`} className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono text-primary">
                            {token.slice(1, -1)}
                          </code>
                        );
                      } else if (token.startsWith('[')) {
                        const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
                        if (linkMatch) {
                          const [, label, href] = linkMatch;
                          const isExternal = /^https?:\/\//.test(href);
                          parts.push(
                            <a
                              key={`${match.index}-link`}
                              href={href}
                              className="text-primary hover:text-primary/80"
                              target={isExternal ? "_blank" : undefined}
                              rel={isExternal ? "noreferrer" : undefined}
                            >
                              {label}
                            </a>
                          );
                        } else {
                          parts.push(token);
                        }
                      }
                      lastIndex = regex.lastIndex;
                    }
                    if (lastIndex < text.length) {
                      parts.push(text.slice(lastIndex));
                    }
                    return parts;
                  };

                  const splitTableRow = (row: string) =>
                    row
                      .split('|')
                      .map((cell) => cell.trim())
                      .filter((cell, idx, arr) => !(cell === "" && (idx === 0 || idx === arr.length - 1)));

                  const tableSeparatorPattern = new RegExp(
                    "^\\s*\\|?[" + "-:" + "\\\\s|" + "]+\\|?\\s*$"
                  );

                  let i = 0;
                  while (i < lines.length) {
                    const line = lines[i];
                    const nextLine = lines[i + 1] ?? "";
                    const isTableHeader = line.includes('|') && /[-:]/.test(nextLine);
                    const isSeparator = tableSeparatorPattern.test(nextLine);

                    if (isTableHeader && isSeparator) {
                      const headers = splitTableRow(line);
                      const rows: string[][] = [];
                      i += 2;
                      while (i < lines.length && lines[i].includes('|')) {
                        const rowLine = lines[i].trim();
                        if (!rowLine) break;
                        rows.push(splitTableRow(rowLine));
                        i += 1;
                      }
                      nodes.push(
                        <div key={`table-${i}`} className="my-6 overflow-x-auto">
                          <table className="w-full border border-border/50 text-sm">
                            <thead className="bg-muted/30">
                              <tr>
                                {headers.map((header, idx) => (
                                  <th key={`th-${idx}`} className="px-3 py-2 text-left font-semibold text-foreground">
                                    {renderInline(header)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row, rowIdx) => (
                                <tr key={`tr-${rowIdx}`} className="border-t border-border/50">
                                  {row.map((cell, cellIdx) => (
                                    <td key={`td-${rowIdx}-${cellIdx}`} className="px-3 py-2 text-muted-foreground">
                                      {renderInline(cell)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                      continue;
                    }

                    // Handle fenced code blocks (```language ... ```)
                    if (line.startsWith('```')) {
                      const language = line.slice(3).trim() || 'text';
                      const codeLines: string[] = [];
                      i += 1;
                      while (i < lines.length && !lines[i].startsWith('```')) {
                        codeLines.push(lines[i]);
                        i += 1;
                      }
                      i += 1; // Skip the closing ```
                      const codeContent = codeLines.join('\n');
                      nodes.push(
                        <CodeBlock key={`code-${i}`} language={language} code={codeContent} />
                      );
                      continue;
                    }

                    // Handle markdown headings
                    if (line.startsWith('# ')) {
                      nodes.push(
                        <h1 key={i} className="text-2xl font-heading font-bold mt-10 mb-4 text-foreground">
                          {line.substring(2)}
                        </h1>
                      );
                      i += 1;
                      continue;
                    }
                    if (line.startsWith('## ')) {
                      const text = line.substring(3);
                      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                      nodes.push(
                        <h2 key={i} id={id} className="text-xl font-heading font-bold mt-8 mb-4 text-foreground scroll-mt-24">
                          {text}
                        </h2>
                      );
                      i += 1;
                      continue;
                    }
                    if (line.startsWith('### ')) {
                      const text = line.substring(4);
                      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                      nodes.push(
                        <h3 key={i} id={id} className="text-lg font-heading font-bold mt-6 mb-3 text-foreground scroll-mt-24">
                          {text}
                        </h3>
                      );
                      i += 1;
                      continue;
                    }

                    // Handle numbered lists
                    if (line.match(/^\d+\.\s/)) {
                      const number = line.match(/^\d+\.?/)?.[0] ?? "";
                      const itemText = line.replace(/^\d+\.\s*/, '');
                      nodes.push(
                        <div key={i} className="flex items-start gap-3 mb-3 ml-4">
                          <span className="text-primary font-mono text-sm">{number}</span>
                          <span className="text-muted-foreground">{renderInline(itemText)}</span>
                        </div>
                      );
                      i += 1;
                      continue;
                    }

                    // Handle list items
                    if (line.startsWith('- ')) {
                      nodes.push(
                        <li key={i} className="ml-6 mb-2 text-muted-foreground list-disc">
                          {renderInline(line.substring(2))}
                        </li>
                      );
                      i += 1;
                      continue;
                    }

                    // Handle horizontal rules
                    if (line.trim() === '---') {
                      nodes.push(<hr key={i} className="my-8 border-border" />);
                      i += 1;
                      continue;
                    }

                    // Handle italic text for calls to action
                    if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) {
                      nodes.push(
                        <p key={i} className="mb-4 italic text-primary/80 border-l-2 border-primary pl-4">
                          {line.slice(1, -1)}
                        </p>
                      );
                      i += 1;
                      continue;
                    }

                    // Regular paragraphs
                    if (line.trim()) {
                      nodes.push(
                        <p key={i} className="mb-4 leading-relaxed text-muted-foreground">
                          {renderInline(line)}
                        </p>
                      );
                    }
                    i += 1;
                  }

                  return nodes;
                })()}
              </div>

              {/* Newsletter Subscribe */}
              <div className="mt-16">
                <NewsletterSubscribe />
              </div>

              {/* Related Posts */}
              {(() => {
                // Extract keywords from current post for relevance scoring
                const extractKeywords = (text: string): string[] => {
                  const commonKeywords = [
                    'vulnerability', 'exploit', 'attack', 'rce', 'privilege escalation',
                    'plugin', 'theme', 'security', 'edge protection', 'malware', 'hack',
                    'wordpress', 'update', 'patch', 'wrap-up', 'weekly', 'report',
                    'bug bounty', 'disclosure', 'elementor', 'woocommerce'
                  ];
                  const lowerText = text.toLowerCase();
                  return commonKeywords.filter(keyword => lowerText.includes(keyword));
                };

                const currentKeywords = extractKeywords(post.title + ' ' + post.excerpt);
                
                // Score and sort posts by relevance
                const scoredPosts = blogPosts
                  .filter(p => p.slug !== slug)
                  .map(p => {
                    const postKeywords = extractKeywords(p.title + ' ' + p.excerpt);
                    const matchingKeywords = currentKeywords.filter(k => postKeywords.includes(k));
                    return { post: p, score: matchingKeywords.length };
                  })
                  .sort((a, b) => b.score - a.score)
                  .slice(0, 3)
                  .map(item => item.post);
                
                const getPostIcon = (title: string) => {
                  if (title.toLowerCase().includes('vulnerability') || title.toLowerCase().includes('exploit')) {
                    return <AlertTriangle className="h-4 w-4 text-amber-400" />;
                  }
                  if (title.toLowerCase().includes('wrap-up') || title.toLowerCase().includes('weekly')) {
                    return <FileWarning className="h-4 w-4 text-primary" />;
                  }
                  return <Shield className="h-4 w-4 text-green-400" />;
                };

                return scoredPosts.length > 0 ? (
                  <div className="mt-12 pt-8 border-t border-border">
                    <h3 className="text-lg font-heading font-bold mb-6 flex items-center gap-2">
                      <span className="text-primary font-mono text-sm">//</span> Related Articles
                    </h3>
                    <div className="grid gap-4 md:grid-cols-3">
                      {scoredPosts.map((relatedPost) => (
                        <Card 
                          key={relatedPost.slug}
                          className="cyber-card border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg group"
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono mb-2">
                              {getPostIcon(relatedPost.title)}
                              <Calendar className="h-3 w-3" />
                              <span>{relatedPost.date}</span>
                            </div>
                            <CardTitle className="text-sm font-heading group-hover:text-primary transition-colors leading-tight line-clamp-2">
                              <Link to={`/blog/${relatedPost.slug}`}>{relatedPost.title}</Link>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="group/btn text-primary hover:text-primary hover:bg-primary/10 font-medium p-0 h-auto text-xs" 
                              asChild
                            >
                              <Link to={`/blog/${relatedPost.slug}`}>
                                Read More
                                <ArrowRight className="ml-1 h-3 w-3 group-hover/btn:translate-x-1 transition-transform" />
                              </Link>
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Back to Blog Footer */}
              <div className="mt-12 pt-8 border-t border-border flex items-center justify-between">
                <Button variant="outline" className="border-primary/30 hover:border-primary hover:bg-primary/10" asChild>
                  <Link to="/blog">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    View All Articles
                  </Link>
                </Button>
                
                <div className="text-sm text-muted-foreground font-mono">
                  // ShadowScan SOC
                </div>
              </div>
              </div>
            </div>
          </div>
        </article>
        <button
          className={`fixed bottom-6 right-6 z-50 inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-secondary/80 text-foreground shadow-lg backdrop-blur transition duration-200 hover:text-primary ${
            showBackToTop ? "opacity-100 scale-100" : "pointer-events-none opacity-0 scale-95"
          }`}
          type="button"
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
